import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Card, 
  Stack, 
  TextField, 
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  useTheme,
  alpha,
  Collapse,
  IconButton
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { 
  Email as EmailIcon, 
  Security as SecurityIcon, 
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';

interface PasswordlessLoginFormProps {
  titleId?: string;
  subtitleId?: string;
  // eslint-disable-next-line no-unused-vars
  onLogin: (email: string) => void;
  isLoading?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  errorMessage?: string;
  onReset?: () => void;
  showHowItWorksSection?: boolean;
}

export const PasswordlessLoginForm: React.FC<PasswordlessLoginFormProps> = ({
  titleId = 'passwordless_login.title',
  subtitleId = 'passwordless_login.subtitle',
  onLogin,
  isLoading = false,
  isSuccess = false,
  isError = false,
  errorMessage = '',
  onReset,
  showHowItWorksSection = true,
}) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  
  // Enhanced email validation regex
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  const validateEmail = (email: string): boolean | string => {
    if (!email) {
      return formatMessage({ id: 'passwordless_login.email_required' });
    }
    
    // Check basic format
    if (!emailRegex.test(email)) {
      return formatMessage({ id: 'passwordless_login.email_invalid' });
    }
    
    // Additional validations
    const [localPart, domain] = email.split('@');
    
    // Check local part length (before @)
    if (localPart.length > 64) {
      return formatMessage({ id: 'passwordless_login.email_invalid' });
    }
    
    // Check domain length
    if (domain && domain.length > 253) {
      return formatMessage({ id: 'passwordless_login.email_invalid' });
    }
    
    // Check for consecutive dots
    if (email.includes('..')) {
      return formatMessage({ id: 'passwordless_login.email_invalid' });
    }
    
    // Check if email starts or ends with a dot
    if (localPart.startsWith('.') || localPart.endsWith('.')) {
      return formatMessage({ id: 'passwordless_login.email_invalid' });
    }
    
    // Check for valid domain extension
    if (domain && !domain.includes('.')) {
      return formatMessage({ id: 'passwordless_login.email_invalid' });
    }
    
    return true;
  };
  
  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
    watch,
  } = useForm({
    defaultValues: {
      email: '',
    },
    mode: 'onTouched',
  });

  const emailValue = watch('email');

  const onSubmit = (data: { email: string }) => {
    // Perform final validation before submitting
    const validationResult = validateEmail(data.email);
    if (validationResult === true && isValid) {
      onLogin(data.email.toLowerCase().trim());
    }
  };

  const handleReset = () => {
    if (onReset) {
      onReset();
    }
  };

  return (
    <Card 
      elevation={0} 
      sx={{ 
        padding: 4, 
        border: '1px solid',
        borderColor: 'divider',
        maxWidth: 480,
        width: '100%',
        margin: '0 auto',
      }}
    >
      <Stack spacing={3}>
        {/* Header with icon */}
        <Stack direction="row" alignItems="center" spacing={2}>
          <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {formatMessage({ id: titleId })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatMessage({ id: subtitleId })}
            </Typography>
          </Box>
        </Stack>

        <Divider />

        {/* Success state */}
        {isSuccess && (
          <Stack spacing={3}>
            <Paper 
              sx={{ 
                p: 3, 
                backgroundColor: alpha(theme.palette.success.main, 0.05),
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
              }}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <CheckCircleIcon sx={{ color: 'success.main', mt: 0.5 }} />
                <Box>
                  <Typography variant="h6" color="success.dark" gutterBottom>
                    {formatMessage({ id: 'passwordless_login.check_email_title' })}
                  </Typography>
                  <Typography variant="body1" color="text.primary" gutterBottom>
                    {formatMessage({ id: 'passwordless_login.check_email_message' })}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    fontWeight="bold" 
                    sx={{ 
                      backgroundColor: alpha(theme.palette.success.main, 0.1),
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      display: 'inline-block',
                      mt: 1
                    }}
                  >
                    {emailValue}
                  </Typography>
                  <Typography variant="body2" color="text.primary" sx={{ mt: 2 }}>
                    {formatMessage({ id: 'passwordless_login.link_expiry' })}
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <Alert severity="info" variant="outlined">
              <Typography variant="body2">
                <strong>{formatMessage({ id: 'passwordless_login.no_email_received' })}</strong> {formatMessage({ id: 'passwordless_login.check_spam' })}
              </Typography>
            </Alert>

            {onReset && (
              <Button
                onClick={handleReset}
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<ArrowBackIcon />}
                size="large"
              >
                {formatMessage({ id: 'passwordless_login.back_to_login' })}
              </Button>
            )}
          </Stack>
        )}

        {/* Error state */}
        {isError && !isSuccess && (
          <Alert 
            severity="error" 
            variant="filled"
            action={
              onReset && (
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={handleReset}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              )
            }
          >
            <Typography variant="body2">
              <strong>{formatMessage({ id: 'passwordless_login.error_title' })}</strong><br />
              {errorMessage ? (
                <span>
                  {formatMessage({ id: 'passwordless_login.error_message' })}<br />
                  <strong>Server response:</strong> {errorMessage}
                </span>
              ) : (
                formatMessage({ id: 'passwordless_login.error_message' })
              )}
            </Typography>
          </Alert>
        )}

        {/* Login form */}
        {!isSuccess && (
          <Stack spacing={3}>
            {/* How it works section */}
            {showHowItWorksSection && (
              <Paper 
                sx={{ 
                  p: 2, 
                  backgroundColor: alpha(theme.palette.info.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                  cursor: 'pointer'
                }}
                onClick={() => setShowHowItWorks(!showHowItWorks)}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <InfoIcon sx={{ color: 'info.main', fontSize: 20 }} />
                    <Typography variant="body2" color="info.dark" fontWeight="medium">
                      {formatMessage({ id: 'passwordless_login.how_it_works_title' })}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="info.main">
                    {showHowItWorks ? formatMessage({ id: 'passwordless_login.hide' }) : formatMessage({ id: 'passwordless_login.learn_more' })}
                  </Typography>
                </Stack>
                
                <Collapse in={showHowItWorks}>
                  <Box sx={{ mt: 2, pl: 3.5 }}>
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1.5}>
                        <Typography variant="body2" color="info.dark" fontWeight="bold">1.</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatMessage({ id: 'passwordless_login.step1' })}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1.5}>
                        <Typography variant="body2" color="info.dark" fontWeight="bold">2.</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatMessage({ id: 'passwordless_login.step2' })}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1.5}>
                        <Typography variant="body2" color="info.dark" fontWeight="bold">3.</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatMessage({ id: 'passwordless_login.step3' })}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ mt: 1 }}>
                        {formatMessage({ id: 'passwordless_login.benefits' })}
                      </Typography>
                    </Stack>
                  </Box>
                </Collapse>
              </Paper>
            )}

            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="body1" color="text.primary" gutterBottom fontWeight="medium">
                    {formatMessage({ id: 'passwordless_login.enter_email_title' })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatMessage({ id: 'passwordless_login.enter_email_subtitle' })}
                  </Typography>
                </Box>

                <Controller
                  control={control}
                  name="email"
                  rules={{ 
                    required: formatMessage({ id: 'passwordless_login.email_required' }),
                    validate: validateEmail
                  }}
                  render={({ field }) => (
                    <TextField 
                      {...field} 
                      label={formatMessage({ id: 'passwordless_login.email_label' })}
                      type="email" 
                      fullWidth
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      InputProps={{
                        startAdornment: (
                          <EmailIcon sx={{ mr: 1, color: 'action.active' }} />
                        ),
                      }}
                      placeholder={formatMessage({ id: 'passwordless_login.email_placeholder' })}
                      autoComplete="email"
                      autoFocus
                      onBlur={(e) => {
                        // Trim and lowercase on blur
                        const trimmedEmail = e.target.value.trim().toLowerCase();
                        field.onChange(trimmedEmail);
                        field.onBlur();
                      }}
                    />
                  )}
                />

                <Button 
                  type="submit" 
                  variant="contained" 
                  color="primary" 
                  disabled={!isValid || isLoading}
                  fullWidth
                  size="large"
                  sx={{ py: 1.5 }}
                >
                  {isLoading ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={20} color="inherit" />
                      <span>{formatMessage({ id: 'passwordless_login.sending' })}</span>
                    </Stack>
                  ) : (
                    formatMessage({ id: 'passwordless_login.send_link' })
                  )}
                </Button>
              </Stack>
            </form>
          </Stack>
        )}
      </Stack>
    </Card>
  );
};
